import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { usePartnerTheme } from "@/contexts/PartnerThemeContext";
import { useAppShellTranslations } from "@/components/app/i18n/shell";
import { CofaceSeal } from "./sidebar/CofaceSeal";
import { NavGroup, NavSkeleton } from "./sidebar/NavGroup";
import { NavItem } from "./sidebar/NavItem";
import { PartnerReferralLogos } from "./sidebar/PartnerReferralLogos";
import {
  HELP_ITEM,
  NAV_GROUPS,
  NEW_QUERY_PERMISSION,
  SETTINGS_PERMISSION,
  filterNavGroups,
  isAllowed,
  isPathActive,
  type PermissionFlags,
} from "./sidebar/nav-config";
import { NewQueryButton, SidebarBrand, SidebarIconButton } from "./sidebar/SidebarControls";
import { buildSidebarTheme } from "./sidebar/sidebar-theme";
import { translateRole } from "./sidebar/sidebar-utils";
import { SidebarOrgCard } from "./sidebar/SidebarOrgCard";
import { SidebarUserMenu, type SidebarUserInfo } from "./sidebar/SidebarUserMenu";
import { useNewQueryShortcut } from "./sidebar/useNewQueryShortcut";
import { useSidebarCollapsed } from "./sidebar/useSidebarCollapsed";

interface AppSidebarProps extends SidebarUserInfo {
  tenantName: string;
  /** `drawer` (mobile, S16): sempre expandido, botão fechar no lugar de recolher, sem atalho global. */
  variant?: "desktop" | "drawer";
  onClose?: () => void;
}

/** Sidebar v2 do shell de `/app` (docs/features/sidebar-v2/figma-spec.md, decisões em tasks.md). */
export function AppSidebar({ tenantName, variant = "desktop", onClose, ...user }: AppSidebarProps) {
  const shellT = useAppShellTranslations();
  const t = shellT.sidebar;
  const { pathname: currentPath, search } = useLocation();
  const pathname = `${currentPath}${search}`;
  const { tenantRole } = useAuth();
  const permissions = usePermissions();
  const { branding, subPartner, hasPartnerBranding, isPartner } = usePartnerTheme();
  const [storedCollapsed, toggleCollapsed] = useSidebarCollapsed();
  const drawer = variant === "drawer";
  const collapsed = !drawer && storedCollapsed;
  const onNavigate = drawer ? onClose : undefined;

  const { classes, style } = useMemo(
    () => buildSidebarTheme(branding, hasPartnerBranding),
    [branding, hasPartnerBranding],
  );

  // C1: mesmas flags de usePermissions que o menu anterior usava.
  const flags: PermissionFlags = {
    canViewDashboard: permissions.canViewDashboard,
    canViewCompanies: permissions.canViewCompanies,
    canViewReports: permissions.canViewReports,
    canViewCollections: permissions.canViewCollections,
    canViewBilling: permissions.canViewBilling,
    canViewSettings: permissions.canViewSettings,
  };
  const loading = permissions.isLoading;
  const groups = loading ? [] : filterNavGroups(NAV_GROUPS, flags);
  const canNewQuery = !loading && isAllowed(flags, NEW_QUERY_PERMISSION);
  useNewQueryShortcut(canNewQuery && !drawer);

  const role: unknown = tenantRole;
  const roleLabel = translateRole(typeof role === "string" ? role : null, t.roles);
  const helpActive = isPathActive(pathname, HELP_ITEM.path);
  const brand = (
    <SidebarBrand
      rail={collapsed}
      partnerLogo={hasPartnerBranding ? (branding?.logoUrl ?? null) : null}
      partnerName={branding?.nome ?? ""}
      homeLabel={t.nav.dashboard}
      classes={classes}
      onNavigate={onNavigate}
    />
  );
  const newQuery = canNewQuery && (
    <NewQueryButton rail={collapsed} label={t.newQuery} classes={classes} onNavigate={onNavigate} />
  );
  const help = (
    <NavItem
      variant={collapsed ? "rail" : "item"}
      label={t.nav.helpAndSupport}
      to={HELP_ITEM.path}
      icon={HELP_ITEM.icon}
      active={helpActive}
      classes={classes}
      onClick={onNavigate}
    />
  );
  const userMenu = (
    <SidebarUserMenu
      {...user}
      roleLabel={roleLabel}
      canViewSettings={!loading && isAllowed(flags, SETTINGS_PERMISSION)}
      collapsed={collapsed}
      classes={classes}
      labels={t}
    />
  );
  const nav = (
    <nav
      aria-label={t.mainNavigation}
      className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto py-1.5", collapsed ? "gap-1" : "gap-0.5")}
    >
      {loading ? (
        <NavSkeleton collapsed={collapsed} classes={classes} label={t.loading} />
      ) : (
        groups.map((group) => (
          <NavGroup
            key={group.groupKey}
            label={t.groups[group.groupKey]}
            items={group.items}
            collapsed={collapsed}
            pathname={pathname}
            labels={t.nav}
            classes={classes}
            onNavigate={onNavigate}
          />
        ))
      )}
    </nav>
  );

  if (collapsed) {
    return (
      <aside
        className={cn("flex h-screen w-[72px] shrink-0 flex-col items-center gap-1 px-3 pb-3 pt-4", classes.surface)}
        style={style}
      >
        {brand}
        <SidebarIconButton
          rail
          label={shellT.layout.expandMenu}
          icon={ChevronRight}
          classes={classes}
          onClick={toggleCollapsed}
        />
        {newQuery}
        {nav}
        {help}
        {userMenu}
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col px-3 pb-3 pt-4",
        drawer ? "h-full w-full" : "h-screen w-64",
        classes.surface,
      )}
      style={style}
    >
      <div className="flex h-[57px] shrink-0 items-center justify-between">
        {brand}
        {drawer ? (
          <SidebarIconButton rail={false} label={t.closeMenu} icon={X} classes={classes} onClick={() => onClose?.()} />
        ) : (
          <SidebarIconButton
            rail={false}
            label={shellT.layout.collapseMenu}
            icon={ChevronLeft}
            classes={classes}
            onClick={toggleCollapsed}
          />
        )}
      </div>

      <div className="mt-3">
        <SidebarOrgCard
          tenantName={tenantName}
          typeLabel={isPartner ? t.orgTypePartner : t.orgTypeOrganization}
          roleLabel={roleLabel}
          classes={classes}
        />
      </div>

      {newQuery}
      {nav}

      <div className="flex shrink-0 flex-col gap-1.5 pt-2">
        <PartnerReferralLogos
          branding={branding}
          subPartner={subPartner}
          hasPartnerBranding={hasPartnerBranding}
          lightSurface={hasPartnerBranding && branding?.sidebarMode !== "custom"}
          captions={{ partner: shellT.layout.partner, referral: shellT.layout.referral }}
          classes={classes}
        />
        <span aria-hidden="true" className={cn("h-px w-full", classes.divider)} />
        {help}
        {userMenu}
        <CofaceSeal label={t.distributor} classes={classes} />
      </div>
    </aside>
  );
}
